import { chains } from '@emberswap/emberswap-sdk2/dist/constants'
import { Native, Token } from '@emberswap/emberswap-sdk2/dist/entities'
import { ChainId } from '@emberswap/emberswap-sdk2/dist/enums'
import { Currency,Type } from '@emberswap/emberswap-sdk2/dist/types'
// import { buttonIconVariants } from '@sushiswap/ui/components/button'
import React, { FC, memo, useCallback, useMemo, useState } from 'react'
// import { useOtherTokenListsQuery } from 'useContracthooks/useOtherTokenLists'
import { getAddress as _getAddress,isAddress } from 'viem'
import { useAccount } from 'wagmi'

import { classNames } from '../../functions'
import { useIsMounted } from '../../hooks'
import { useTokens } from '../../hooks/Token/useTokens'
import { useBalances } from '../../hooks/useBalance'
import { usePrices } from '../../hooks/usePrices'
import { useCustomTokens, useSettings } from '../../state/user/hooks'
// import { useTokenWithCache } from '../../hooks'
import { SlideIn } from '../Animation/SlideIn'
import { Button } from '../Button'
import { Currency as UICurrency } from '../Currency'
import { Dialog } from '../Dialog'
import { Close, IconButton, NetworkIcon, SearchIcon, XCircleIcon } from '../Icons'
import { DEFAULT_INPUT_PADDING, DEFAULT_INPUT_UNSTYLED, Input } from '../Input'
import { List } from '../List'
import { Loader } from '../Loader'
import { Skeleton } from '../Skeleton'
import { TokenListFilterByQuery } from '../TokenListFilterByQuery'
import { TokenSelectorCurrencyList } from './TokenSelectorCurrencyList'
import { TokenSelectorImportRow } from './TokenSelectorImportRow'
import { TokenSelectorSettingsOverlay } from './TokenSelectorSettingsOverlay'

export interface TokenSelectorProps {
  currency: Type | undefined
  open: boolean
  chainId: ChainId
  onClose: () => void
  onSelect(currency: Type): void
  includeNative?: boolean
  title?: string
  midSelector?: boolean
  hidePinnedTokens?: boolean
  hideSearch?: boolean
  isLoading?: boolean
}
/**
 * @param {string}address 
 * @return {string}
 */
function getAddress(address: string) {
  if (address === 'NATIVE') return 'NATIVE'
  return _getAddress(address)
}

export const TokenSelector: FC<TokenSelectorProps> = memo(function TokenSelector
  ({
    currency,
    open,
    chainId,
    onClose,
    onSelect,
    includeNative = true,
    title,
    midSelector,
    hidePinnedTokens,
    hideSearch,
    isLoading,
  }) {
    const { address } = useAccount()
    const isMounted = useIsMounted()
    const { data: pricesMap } = usePrices({ chainId })
    const [customTokenMap, { addCustomToken, removeCustomToken }] = useCustomTokens(chainId)
    const defaultTokenMap = useTokens(chainId)
    const [{ pinnedTokens: pinnedTokenMap, userPath }, {resetPath, removePath, addPinnedToken, removePinnedToken }] = useSettings(chainId)

    const _tokenMap: Record<string, Token> = useMemo(
      () => ({ ...defaultTokenMap, ...customTokenMap }),
      [defaultTokenMap, customTokenMap]
    )

    const _tokenMapValues = useMemo(() => {
      return Object.values(_tokenMap)
    }, [_tokenMap])

    const { data: balancesMap } = useBalances({
      account: address,
      chainId,
      currencies: _tokenMapValues,
      enabled: open && !midSelector,
    })
    // console.log(balancesMap)
    
    const officialTokenIds = useMemo(() => {
      if (!defaultTokenMap) return []
      return Object.values(defaultTokenMap).map((el) => el.wrapped.address)
    }, [defaultTokenMap])
    
    const pinnedTokens = useMemo(() => {
      return (pinnedTokenMap?.[chainId] ?? [])
        .map((id) => {
          const [, address] = id.split(':')
          if (address === 'NATIVE') return Native.onChain(chainId)
          return defaultTokenMap?.[address] || customTokenMap?.[address]
        })
        .filter((token): token is Token => !!token)
    }, [chainId, customTokenMap, pinnedTokenMap, defaultTokenMap])
    
  const hasToken = useCallback(
    (currency: Currency | string) => {
      if (typeof currency === 'string') {
        if (!currency.includes(':')) {
          throw new Error('Address provided instead of id')
        }

        const [chainId, address] = currency.split(':')
        if (address !== 'NATIVE' && !isAddress(address)) {
          throw new Error('Address provided not a valid ERC20 address')
        }

        return pinnedTokenMap?.[chainId]?.includes(`${chainId}:${getAddress(address)}`)
      }

      return !!pinnedTokenMap?.[currency.chainId]?.includes(currency.id)
    },
    [pinnedTokenMap]
  )
  
   const _onPin = useCallback(
     (currencyId: string) => {
       if (!hasToken(currencyId)) {
         addPinnedToken(currencyId)
       } else {
         removePinnedToken(currencyId)
       }
     },
     [addPinnedToken, hasToken, removePinnedToken]
   )
   
     const isPath = useCallback(
       (currency: Currency) => {
        if (userPath && chainId && userPath[chainId]) {
         return Object.values(userPath[chainId]).some((t) => t.address == currency.wrapped.address)
        }
        return false
      },[chainId, userPath] )

    const _onRemovePath = useCallback((currency: Currency) => {
      removePath && removePath({ chainId: currency.chainId, address: currency.wrapped.address })
      chainId && userPath && Object.values(userPath[chainId]).length < 0 && setActiveTokens(false)
    }, [chainId, removePath, userPath])
    
    const [activeTokens, setActiveTokens] = useState(false)
    
    const handleSelect = useCallback(
      (currency: Type) => {
        onSelect(currency)
        !midSelector && onClose()
      },
      [midSelector, onClose, onSelect]
    )

    const handleImport = useCallback(
      (currency: Token) => {
        addCustomToken(currency)
        handleSelect(currency)
      },
      [handleSelect, addCustomToken]
    )

    return useMemo(() => {
      if (!isMounted) return <></>
      return (
        <TokenListFilterByQuery
          tokenMap={_tokenMap}
          chainId={chainId}
          pricesMap={!midSelector ? pricesMap : undefined}
          balancesMap={!midSelector ? balancesMap : undefined}
          includeNative={includeNative}
        >
          {({ currencies, inputRef, query, onInput, searching, queryToken }) => (
            <Dialog open={open}  onClose={onClose} initialFocus={inputRef}>
              <Dialog.Content className="!max-w-md overflow-hidden !bg-tokenselector  gradient-backdrop-blur !flex flex-col justify-start min-h-[75vh]  pb-[16px]">
                <SlideIn>
                  <Dialog.Header onClose={onClose} title={title}>
                    {!midSelector && (
                      <TokenSelectorSettingsOverlay
                        customTokensMap={customTokenMap}
                        onRemoveToken={removeCustomToken}
                      />
                    )}
                  </Dialog.Header>
                  <>
                    {!hideSearch ? (
                      <div className="relative flex items-center justify-between w-full gap-1 pr-3 my-3 mb-5 ring-offset-2 ring-offset-secondarybg bg-tokenselectorlist rounded-2xl focus-within:ring-2 text-primary ring-transparent">
                        <Input.Address
                          variant="unstyled"
                          id="token-search"
                          ref={inputRef}
                          title="Search token by address or symbol"
                          placeholder="Search token by address or symbol"
                          value={query}
                          onChange={onInput}
                          className={classNames(DEFAULT_INPUT_UNSTYLED, DEFAULT_INPUT_PADDING)}
                          type="text"
                        />
                        {searching ? (
                          <div className="relative left-[-2px]">
                            <Loader.Spinner className="w-3.5 h-3.5 stroke-[3px] animate-spin-slow text-slate-500" />
                          </div>
                        ) : query ? (
                          <XCircleIcon
                            width={20}
                            height={20}
                            className="cursor-pointer text-slate-500 hover:text-slate-300"
                            onClick={() => onInput('')}
                          />
                        ) : (
                          <SearchIcon
                            className="text-slate-500"
                            strokeWidth={2}
                            width={20}
                            height={20}
                          />
                        )}
                      </div>
                    ) : null}

                    {pinnedTokens && pinnedTokens.length > 0 && !hidePinnedTokens && (
                      <div className="flex flex-wrap gap-1.5">
                        {pinnedTokens.map((token) => {
                          if (!includeNative && token.isNative) return <></>
                          return (
                            <div key={token.id} className=" group">
                              <Button
                                size="sm"
                                variant="filled"
                                color="gray"
                                className="group !rounded-xl !text-xs !font-medium"
                                key={token.id}
                                onClick={() => handleSelect(token)}
                              >
                                <UICurrency.Icon
                                  width={20}
                                  height={20}
                                  // className={buttonIconVariants({ size: 'default' })}
                                  currency={token}
                                  disableLink
                                />
                                {!includeNative &&
                                token.symbol === Native.onChain(token.chainId).wrapped.symbol
                                  ? Native.onChain(token.chainId).symbol
                                  : token.symbol}
                                <IconButton
                                  key={token.id}
                                  name="remove"
                                  onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                    e.stopPropagation()
                                    _onPin(token.id)
                                  }}
                                >
                                  <Close width={16} />
                                </IconButton>
                              </Button>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </>

                  <div className="relative grid pt-2 ">
                    {!midSelector ? (
                      <a className="text-xs text-right pr-16 mb-0.5 text-primary justify-self-end">
                        {address && 'Balances'}
                      </a>
                    ) : (
                      <div className="flex items-center w-full gap-4 px-4 -mb-1 text-xs justify-items-center">
                        <a
                          className={classNames(
                            'cursor-pointer ',
                            !activeTokens ? ' text-slate-200 font-medium' : 'text-slate-400'
                          )}
                          onClick={() => setActiveTokens(false)}
                        >
                          All Tokens
                        </a>
                        <a
                          className={classNames(
                            activeTokens ? 'text-slate-200 font-medium' : 'text-slate-400',
                            chainId &&
                              userPath &&
                              userPath[chainId] &&
                              Object.values(userPath[chainId]).length > 0
                              ? 'cursor-pointer'
                              : 'cursor-not-allowed'
                          )}
                          onClick={() => {
                            chainId &&
                              userPath &&
                              userPath[chainId] &&
                              Object.values(userPath[chainId])?.length > 0 &&
                              setActiveTokens(true)
                          }}
                        >
                          Active Tokens
                        </a>
                        <Button
                          onClick={() => resetPath && chainId && resetPath(chainId)}
                          className="!px-2 !py-1 !h-auto ml-auto mb-1 hover:text-slate-300  hover:font-bold transition-all duration-500 !font-medium text-slate-400/60  hover:from-orange-500/70 hover:via-red-500/70 hover:to-yellow-500/70 from-orange-500/40 via-red-500/40 to-yellow-500/40 bg-gradient-to-t"
                        >
                          Reset Path
                        </Button>
                      </div>
                    )}
                  </div>
                  <List.Control
                    className={classNames(
                      midSelector ? 'mt-2' : 'px-1 py-1',
                      'relative flex flex-col flex-1 flex-grow !bg-tokenselectorlist rounded-xl'
                    )}
                  >
                    {queryToken[0] && (
                      <TokenSelectorImportRow
                        className="!px-6"
                        currencies={queryToken}
                        onImport={() => queryToken[0] && handleImport(queryToken[0])}
                      />
                    )}
                    {
                      /* isQueryTokenLoading ||*/ isLoading ? (
                        <div className="py-0.5 h-[64px] -mb-3">
                          <div className="flex items-center w-full h-full px-3 rounded-lg">
                            <div className="flex items-center justify-between flex-grow gap-2 rounded">
                              <div className="flex flex-row items-center flex-grow gap-4">
                                <Skeleton.Circle radius={40} />
                                <div className="flex flex-col items-start">
                                  <Skeleton.Text className="w-full" />
                                  <Skeleton.Text fontSize="sm" className="w-full" />
                                </div>
                              </div>

                              <div className="flex flex-col w-full">
                                <Skeleton.Text className="w-[80px]" />
                                <Skeleton.Text fontSize="sm" align="right" className="w-[40px]" />
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <TokenSelectorCurrencyList
                            disableNative={!includeNative}
                            midSelector={midSelector}
                            balancesMap={balancesMap}
                            userPath={userPath}
                            selected={currency}
                            onSelect={handleSelect}
                            pin={{
                              onPin: _onPin,
                              isPinned: hasToken,
                            }}
                            onPath={{
                              onRemovePath: _onRemovePath,
                              isPath: isPath,
                            }}
                            officialTokenIds={officialTokenIds}
                            currencies={
                              activeTokens && midSelector
                                ? currencies.filter(
                                    (c) =>
                                      chainId &&
                                      userPath &&
                                      userPath[chainId] &&
                                      Object.values(userPath[chainId]).some(
                                        (t) => t.address == c.wrapped.address
                                      )
                                  )
                                : currencies
                            }
                            chainId={chainId}
                          />
                          {currencies.length === 0 && !queryToken && chainId && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="flex flex-col items-center justify-center gap-1">
                                <a className="flex text-xs italic text-slate-500">
                                  No tokens found on
                                </a>
                                <a className="flex gap-1 text-xs italic font-medium text-slate-500">
                                  <NetworkIcon width={14} height={14} chainId={chainId} />{' '}
                                  {chains[chainId].name}
                                </a>
                              </div>
                            </div>
                          )}
                        </>
                      )
                    }
                  </List.Control>
                </SlideIn>
              </Dialog.Content>
            </Dialog>
          )}
        </TokenListFilterByQuery>
      )
    }, [isMounted, _tokenMap, chainId, midSelector, pricesMap, balancesMap, includeNative, open, onClose, title, customTokenMap, removeCustomToken, hideSearch, pinnedTokens, hidePinnedTokens, address, activeTokens, userPath, isLoading, currency, handleSelect, _onPin, hasToken, _onRemovePath, isPath, officialTokenIds, resetPath, handleImport])
  }
)
